import React, { useState } from 'react';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import Reveal from './Reveal';

const PROJECT_TYPES = ['New build', 'Refurbishment', 'Extension', 'Conversion'];

const WEB3FORMS_ACCESS_KEY = '218c02ca-e1cf-4fff-9654-dc8c9b8a5469';

const emptyForm = { name: '', email: '', number: '', address: '', projectType: '' };

const ContactForm = () => {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const setField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Please enter your name';
    if (!form.email.trim()) e.email = 'Please enter your email';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email';
    if (!form.number.trim()) e.number = 'Please enter your number';
    if (!form.address.trim()) e.address = 'Please enter the project address';
    if (!form.projectType) e.projectType = 'Please choose a project type';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitError('');
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('access_key', WEB3FORMS_ACCESS_KEY);
      fd.append('subject', `New estimate enquiry — ${form.projectType} (${form.name})`);
      fd.append('from_name', 'Core Estimating Website');
      fd.append('name', form.name);
      fd.append('email', form.email);
      fd.append('phone', form.number);
      fd.append('project_type', form.projectType);
      fd.append('project_address', form.address);
      fd.append(
        'message',
        `New enquiry from ${form.name}\n\nPhone: ${form.number}\nProject type: ${form.projectType}\nProject address: ${form.address}`
      );

      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: fd,
      });
      const data = await res.json();
      if (data.success) {
        setSubmitted(true);
      } else {
        setSubmitError(data.message || 'Something went wrong. Please try again or call us.');
      }
    } catch (_) {
      setSubmitError('Could not send right now. Please check your connection or call us.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="enquire" className="bg-[#262629] px-5 md:px-10 py-20 md:py-24">
      <div className="max-w-[1400px] mx-auto">
        <Reveal>
          <div className="rounded-2xl bg-[#353539] border border-white/5 p-7 md:p-12">
            {submitted ? (
              <div className="text-center py-10">
                <CheckCircle2 className="w-16 h-16 text-yellow mx-auto mb-6" strokeWidth={1.8} />
                <h2 className="font-display text-3xl md:text-4xl mb-3">Thanks, {form.name.split(' ')[0]}!</h2>
                <p className="text-white/80 text-base md:text-lg max-w-md mx-auto">
                  We've received your enquiry and one of our team will be in touch about your{' '}
                  <span className="text-yellow font-semibold">{form.projectType.toLowerCase()}</span> project shortly.
                </p>
                <button
                  onClick={() => { setForm(emptyForm); setSubmitted(false); }}
                  className="mt-8 text-yellow font-semibold hover:underline"
                >
                  Send another enquiry
                </button>
              </div>
            ) : (
              <>
                <div className="mb-8">
                  <h2 className="font-display text-yellow text-4xl md:text-5xl mb-3">Get a FREE fee proposal</h2>
                  <p className="text-white/75 text-base md:text-lg max-w-xl">
                    Tell us a little about your project and we'll get back to you with the right kit for the job.
                  </p>
                </div>

                <form onSubmit={handleSubmit} noValidate className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="name" className="text-white/90">Name</Label>
                    <Input
                      id="name"
                      value={form.name}
                      onChange={(e) => setField('name', e.target.value)}
                      placeholder="Your full name"
                      className="bg-[#262629] border-white/15 text-white placeholder:text-white/40 h-12 focus-visible:ring-[#ff7a1a]"
                    />
                    {errors.name && <span className="text-[#ff8f4a] text-sm">{errors.name}</span>}
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label htmlFor="email" className="text-white/90">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setField('email', e.target.value)}
                      placeholder="you@example.com"
                      className="bg-[#262629] border-white/15 text-white placeholder:text-white/40 h-12 focus-visible:ring-[#ff7a1a]"
                    />
                    {errors.email && <span className="text-[#ff8f4a] text-sm">{errors.email}</span>}
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label htmlFor="number" className="text-white/90">Number</Label>
                    <Input
                      id="number"
                      type="tel"
                      value={form.number}
                      onChange={(e) => setField('number', e.target.value)}
                      placeholder="07000 000000"
                      className="bg-[#262629] border-white/15 text-white placeholder:text-white/40 h-12 focus-visible:ring-[#ff7a1a]"
                    />
                    {errors.number && <span className="text-[#ff8f4a] text-sm">{errors.number}</span>}
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label htmlFor="projectType" className="text-white/90">Project type</Label>
                    <Select value={form.projectType} onValueChange={(v) => setField('projectType', v)}>
                      <SelectTrigger
                        id="projectType"
                        className="bg-[#262629] border-white/15 text-white h-12 data-[placeholder]:text-white/40 focus:ring-[#ff7a1a]"
                      >
                        <SelectValue placeholder="Select a project type" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#2d2d31] border-white/10 text-white">
                        {PROJECT_TYPES.map((t) => (
                          <SelectItem key={t} value={t} className="focus:bg-white/10 focus:text-yellow">
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.projectType && <span className="text-[#ff8f4a] text-sm">{errors.projectType}</span>}
                  </div>

                  <div className="flex flex-col gap-2 md:col-span-2">
                    <Label htmlFor="address" className="text-white/90">Project address</Label>
                    <Textarea
                      id="address"
                      value={form.address}
                      onChange={(e) => setField('address', e.target.value)}
                      placeholder="Site address, town and postcode"
                      rows={3}
                      className="bg-[#262629] border-white/15 text-white placeholder:text-white/40 focus-visible:ring-[#ff7a1a] resize-none"
                    />
                    {errors.address && <span className="text-[#ff8f4a] text-sm">{errors.address}</span>}
                  </div>

                  <div className="md:col-span-2">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="inline-flex items-center gap-3 bg-jaw-yellow text-[#262629] font-semibold text-base px-8 py-4 rounded-[10px] hover:bg-[#e8660a] transition-colors group disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {submitting ? 'Sending…' : 'Send enquiry'}
                      {!submitting && <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" strokeWidth={2.5} />}
                    </button>
                    {submitError && <p className="text-[#ff8f4a] text-sm mt-3">{submitError}</p>}
                  </div>
                </form>
              </>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default ContactForm;
